import { getLocalCartUuid, updateLocalCartUuid } from '@iheartjane/cart/utils';
import {
  clearCartUser as patchCartUser,
  recoverUserCart as fetchUserCart,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { trackError } from '@iheartjane/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  getCartForUser,
  removeCartUser,
} from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp, useHeadlessApp } from '@jane/shared/providers';

export interface CartUserActions {
  clearCartUser: () => void;
  recoverUserCart: () => void;
}

export const useCartUser = (): CartUserActions => {
  const dispatch = useCustomerDispatch();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  const { partnerId } = useCustomerSelector(selectEmbeddedApp);
  const { headlessStoreId } = useHeadlessApp();
  const queryClient = useQueryClient();

  return useMemo(
    () =>
      // TODO(carts-v3): delete the flag check and v1 branch with the cart slice.
      useV3Cart
        ? {
            recoverUserCart: () => {
              const localUuid = getLocalCartUuid(appMode, partnerId);

              fetchUserCart(queryClient, {
                appMode,
                jwt: getJwt(),
                storeId: headlessStoreId ?? partnerId,
                uuid: localUuid ?? undefined,
              }).then(
                (cart) => {
                  if (cart) updateLocalCartUuid(appMode, partnerId, cart.uuid);
                  else if (localUuid)
                    updateLocalCartUuid(appMode, partnerId, null);
                },
                // Unlike v1, keep the bag: a transient failure shouldn't delete it.
                (error) => trackError(error, { cartUuid: localUuid })
              );
            },
            clearCartUser: () => {
              const uuid = getLocalCartUuid(appMode, partnerId);
              if (!uuid) return;

              patchCartUser(queryClient, {
                appMode,
                jwt: getJwt(),
                uuid,
              }).catch((error) => trackError(error, { cartUuid: uuid }));
            },
          }
        : {
            recoverUserCart: () => dispatch(getCartForUser()),
            clearCartUser: () => dispatch(removeCartUser()),
          },
    [appMode, dispatch, headlessStoreId, partnerId, queryClient, useV3Cart]
  );
};
